import React, { useState } from 'react';
import { 
  ChevronLeft, Truck, Package, CheckCircle, Clock, CreditCard, 
  MapPin, User, Weight, DollarSign, ChevronRight
} from 'lucide-react';

const DELIVERY_STATUS_CONFIG = {
  '待送货': { 
    color: 'bg-slate-500', 
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
    icon: Clock,
    activeColor: 'bg-slate-500'
  },
  '已到场': { 
    color: 'bg-blue-500', 
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    icon: Truck,
    activeColor: 'bg-blue-500'
  },
  '已入库': { 
    color: 'bg-purple-500', 
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    icon: Package,
    activeColor: 'bg-purple-500'
  },
  '已结算': { 
    color: 'bg-amber-500', 
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    icon: DollarSign,
    activeColor: 'bg-amber-500'
  },
  '已付款': { 
    color: 'bg-green-500', 
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    icon: CreditCard,
    activeColor: 'bg-green-500'
  },
};

const STATUS_ORDER = ['待送货', '已到场', '已入库', '已结算', '已付款'];

const MOCK_ORDER = {
  orderNo: 'ORD20260218001',
  status: '送货中',
  baseName: '马钢诚兴马鞍山基地',
  baseAddress: '安徽省马鞍山市雨山区',
  material: '重型废钢 (HMS1)',
  estimatedWeight: 50,
  estimatedAmount: 132500,
  createdAt: '2026-02-18 09:30',
  deliveries: [
    { 
      id: 'D001', 
      deliveryNo: 'DEL20260218001', 
      status: '已付款', 
      weight: 10.2, 
      amount: 27030, 
      vehicleNo: '皖E12345', 
      driverName: '张师傅', 
      driverPhone: '138****1234',
      timeline: [
        { time: '02-18 10:00', status: '待送货', action: '创建预约单' },
        { time: '02-18 11:30', status: '已到场', action: '车辆到场' },
        { time: '02-18 14:00', status: '已入库', action: '入库过磅 10.2吨' },
        { time: '02-18 16:00', status: '已结算', action: '结算确认 ¥27,030' },
        { time: '02-18 18:00', status: '已付款', action: '付款完成' },
      ]
    },
    { 
      id: 'D002', 
      deliveryNo: 'DEL20260218002', 
      status: '已入库', 
      weight: 12.3, 
      amount: null, 
      vehicleNo: '皖E23456', 
      driverName: '李师傅', 
      driverPhone: '139****5678',
      timeline: [
        { time: '02-18 14:30', status: '待送货', action: '创建预约单' },
        { time: '02-18 15:00', status: '已到场', action: '车辆到场' },
        { time: '02-18 17:00', status: '已入库', action: '入库过磅 12.3吨' },
      ]
    },
    { 
      id: 'D003', 
      deliveryNo: 'DEL20260218003', 
      status: '已到场', 
      weight: null, 
      amount: null, 
      vehicleNo: '皖E34567', 
      driverName: '王师傅', 
      driverPhone: '137****9012',
      timeline: [
        { time: '02-18 16:00', status: '待送货', action: '创建预约单' },
        { time: '02-18 17:30', status: '已到场', action: '车辆到场' },
      ]
    },
  ]
};

const StatusFlow = ({ currentStatus }) => {
  const currentIndex = STATUS_ORDER.indexOf(currentStatus);
  
  return (
    <div className="flex items-center justify-between px-2">
      {STATUS_ORDER.map((status, index) => {
        const config = DELIVERY_STATUS_CONFIG[status];
        const Icon = config.icon;
        const isCompleted = index < currentIndex;
        const isCurrent = index === currentIndex;
        
        return (
          <React.Fragment key={status}>
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                isCompleted ? 'bg-green-500' : 
                isCurrent ? config.activeColor : 'bg-slate-200'
              }`}>
                {isCompleted ? (
                  <CheckCircle size={16} className="text-white" />
                ) : (
                  <Icon size={14} className={isCurrent ? 'text-white' : 'text-slate-400'} />
                )}
              </div>
              <span className={`text-[10px] mt-1 font-medium ${
                isCurrent ? 'text-slate-800' : 'text-slate-400'
              }`}>
                {status}
              </span>
            </div>
            {index < STATUS_ORDER.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 ${
                index < currentIndex ? 'bg-green-500' : 'bg-slate-200'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const DeliveryCard = ({ delivery, index, total }) => {
  const [expanded, setExpanded] = useState(false);
  const config = DELIVERY_STATUS_CONFIG[delivery.status];
  const Icon = config.icon;
  
  return (
    <div className={`rounded-xl border ${config.borderColor} ${config.bgColor} overflow-hidden mb-3`}>
      <div 
        className="p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center`}>
              <Icon size={20} className="text-white" />
            </div>
            <div>
              <div className="font-bold text-slate-800 text-sm">{delivery.deliveryNo}</div>
              <div className="text-xs text-slate-500">批次 {index + 1}/{total}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${config.color} text-white`}>
              {delivery.status}
            </span>
            <ChevronRight 
              size={16} 
              className={`text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} 
            />
          </div>
        </div>
        
        <StatusFlow currentStatus={delivery.status} />
        
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200/50">
          <div className="flex items-center gap-4 text-xs text-slate-600">
            {delivery.weight && (
              <span className="flex items-center gap-1">
                <Weight size={12} />
                {delivery.weight}吨
              </span>
            )}
            {delivery.amount && (
              <span className="flex items-center gap-1 text-red-600 font-bold">
                <DollarSign size={12} />
                ¥{delivery.amount.toLocaleString()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <User size={12} />
            {delivery.driverName}
          </div>
        </div>
      </div>
      
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-200/50 bg-white/50">
          <div className="pt-3 space-y-2">
            {delivery.timeline.map((item, i) => {
              const itemConfig = DELIVERY_STATUS_CONFIG[item.status];
              const isLast = i === delivery.timeline.length - 1;
              
              return (
                <div key={i} className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-2 h-2 rounded-full ${itemConfig.activeColor}`} />
                    {!isLast && <div className="w-0.5 h-6 bg-slate-200" />}
                  </div>
                  <div className="flex-1 pb-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">{item.time}</span>
                      <span className={`text-xs font-medium ${itemConfig.color.replace('bg-', 'text-').replace('-500', '-600')}`}>
                        {item.status}
                      </span>
                    </div>
                    <div className="text-sm text-slate-700 mt-0.5">{item.action}</div>
                  </div>
                  <button className="text-xs text-blue-500 font-medium">查看</button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default function OrderDetailPrototype1({ onBack }) {
  const order = MOCK_ORDER;
  
  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={onBack} className="flex items-center gap-1 text-slate-600">
            <ChevronLeft size={20} />
            <span>返回</span>
          </button>
          <h1 className="font-bold text-slate-800">订单详情</h1>
          <div className="w-16" />
        </div>
      </div>
      
      <div className="p-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 mb-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="font-bold text-slate-800">{order.orderNo}</div>
              <div className="text-xs text-slate-500 mt-1">{order.createdAt}</div>
            </div>
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-500 text-white">
              {order.status}
            </span>
          </div>
          
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-slate-600">
              <MapPin size={14} className="text-slate-400" />
              <span>{order.baseName}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Package size={14} className="text-slate-400" />
              <span>{order.material}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Weight size={14} className="text-slate-400" />
              <span>预估 {order.estimatedWeight}吨 · 预估 ¥{order.estimatedAmount.toLocaleString()}</span>
            </div>
          </div>
        </div>
        
        <div className="mb-3">
          <h2 className="font-bold text-slate-800 mb-2">送货单列表</h2>
          <p className="text-xs text-slate-500">点击展开查看详细状态流转</p>
        </div>
        
        {order.deliveries.map((delivery, index) => (
          <DeliveryCard 
            key={delivery.id} 
            delivery={delivery} 
            index={index} 
            total={order.deliveries.length} 
          />
        ))}
      </div>
      
      <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-slate-200 p-4">
        <button className="w-full bg-blue-500 text-white py-3 rounded-xl font-bold">
          新增送货单
        </button>
      </div>
    </div>
  );
}